/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ButtonResponsiveWrapper,
  Enrollment,
  Inscription,
  Preloader,
  PriceInput,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';

import AppButton from '@/components/controls/AppButton';
import { ModalWrapper } from '@/components/wrappers';

import { useInvestmentsValidation } from '../hooks';
import { InvestmentCardBlock } from '../InvestmentCardBlock';
import { useInvestmentFundingQuery } from '../queries';
import { useFundingAmountStore } from '../StartInvesting/FundingAmount/stores/useFundingAmount.store';

import ServerErrorModal from './ServerErrorModal';

const BUTTON_WIDTH = '130px';

interface Props {
  visible: boolean;
  isSwitchMode?: boolean;
  onSetVisible: (value: boolean) => void;
  currentMyAccount: Enrollment;
  investmentAmount?: number | string;
  buySellMode?: boolean;
  onRefetch?: () => void;
  setInvestmentAmount?: (amount: number) => void;
  onSave?: (value: any) => void;
}
const EditAmountModal: React.FC<Props> = ({
  visible,
  currentMyAccount,
  investmentAmount,
  onSetVisible,
  buySellMode,
  onRefetch,
  setInvestmentAmount,
  onSave,
  isSwitchMode,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [showErrors, setShowErrors] = useState(false);
  const [errorWarningVisible, setErrorWarningVisible] = useState(false);
  const { handleSetValues: handleSetAmountValues } = useFundingAmountStore();
  const [investingAmount, setInvestingAmount] = useState<string>('');
  const { handleSave: onSaveInvestmentAmount, isLoading } = useInvestmentFundingQuery({
    onSuccess: (value) => {
      if (setInvestmentAmount) {
        setInvestmentAmount(_.toNumber(investingAmount));
      }

      handleSetAmountValues({ fundingAmount: String(value) });
      onSetVisible(false);
      setErrorWarningVisible(false);
      if (onRefetch) onRefetch();
    },
    onError: () => setErrorWarningVisible(true),
  });
  const availableToInvest = useMemo(() => {
    if (currentMyAccount?.available - currentMyAccount?.investmentThreshold > 0) {
      return currentMyAccount?.available - currentMyAccount?.investmentThreshold;
    }
    return 0;
  }, [currentMyAccount]);
  const { error, setError } = useInvestmentsValidation({
    investingAmount,
    availableToInvest,
    minimumInvestmentAmount: currentMyAccount?.minimumInvestmentAmount,
  });
  const handleMaximumAllowed = useCallback(() => {
    setInvestingAmount(String(availableToInvest));
  }, [availableToInvest]);
  const handleSubmit = useCallback(() => {
    setShowErrors(true);
    if (error) {
      return null;
    }
    if (isSwitchMode && setInvestmentAmount) {
      setInvestmentAmount(_.toNumber(investingAmount));
      onSetVisible(false);
      return;
    }
    if (onSave && buySellMode) {
      onSetVisible(false);
      if (onRefetch) onRefetch();
      return onSave({ fundingAmount: investingAmount });
    }
    return onSaveInvestmentAmount({
      amount: investingAmount,
    });
  }, [
    buySellMode,
    error,
    investingAmount,
    isSwitchMode,
    onRefetch,
    onSave,
    onSaveInvestmentAmount,
    onSetVisible,
    setInvestmentAmount,
  ]);

  useEffect(() => {
    if (investmentAmount) {
      setInvestingAmount(String(investmentAmount));
    }
  }, [investmentAmount]);

  return (
    <ModalWrapper
      title={t('Editing Funding Amount')}
      visible={visible}
      onSetVisible={onSetVisible}
      testId="EditAmountModal-wrapper-id"
    >
      <Box direction="column">
        <Box
          direction="column"
          justify="center"
          background="module"
          round="moduleRound"
          pad={isMobile ? 'spacing12' : 'spacing24'}
          margin={{ bottom: 'spacing24' }}
        >
          <Box direction="row" justify="center">
            {currentMyAccount && (
              <InvestmentCardBlock
                {...(isMobile
                  ? {
                      width: {
                        min: 'auto',
                      },
                    }
                  : {})}
                enrollment={currentMyAccount}
              />
            )}
          </Box>
          <Box
            direction="column"
            pad={isMobile ? 'spacing16' : { horizontal: 'spacing24', top: '28px', bottom: '18px' }}
            background="canvas"
            round="container1Round"
            margin={{ top: isMobile ? 'spacing12' : 'spacing24' }}
            width={isMobile ? {} : { max: '100%', min: '500px' }}
            data-testid="EnrollmentItem-wrapper"
            border={{ size: 'small', color: 'border2' }}
            elevation="default"
          >
            <Text margin={{ bottom: 'spacing12' }} size="xxlarge">
              {t('Funding amount')}
            </Text>
            <Text margin={{ bottom: isMobile ? 'spacing12' : 'spacing24' }} size="medium">
              {t(
                'The amount will be transferred from your cash account to your investment account after you confirm the investment.'
              )}
            </Text>
            <Box direction={isMobile ? 'column' : 'row'} align={isMobile ? 'start' : 'center'}>
              <Box width={isMobile ? 'auto' : '280px'} margin={{ right: 'spacing24' }}>
                <PriceInput
                  name="Funding amount"
                  isClearMode
                  placeholder={t('Enter $ amount')}
                  onChange={setInvestingAmount}
                  onIconClick={() => setInvestingAmount('')}
                  value={_.toString(investingAmount)}
                  disabled={isLoading}
                  autoFocus
                />
              </Box>
              <Inscription
                margin={isMobile ? { top: 'spacing12' } : {}}
                onClick={handleMaximumAllowed}
                cursor="pointer"
                size="medium"
                color="textAccent"
              >
                {t('Maximum Allowed')}
              </Inscription>
            </Box>
          </Box>
        </Box>
        {error && showErrors && (
          <Box align="end" width="100%" margin={{ bottom: 'spacing24' }}>
            <Text size="medium" color="danger" style={{ lineHeight: '20px' }}>
              {t(error)}
            </Text>
          </Box>
        )}
        <ButtonResponsiveWrapper justify="end">
          <AppButton
            fill={isMobile}
            testId="edit_amount_modal_cancel"
            buttonType="secondary"
            width={BUTTON_WIDTH}
            onClick={() => {
              onSetVisible(false);
              setShowErrors(false);
              setError('');
              setInvestingAmount(String(investmentAmount));
            }}
          >
            {t('Cancel')}
          </AppButton>

          <AppButton
            fill={isMobile}
            testId="edit_amount_modal_submit"
            disabled={isLoading}
            width={BUTTON_WIDTH}
            onClick={handleSubmit}
          >
            {isLoading ? <Preloader color="white" /> : t('Submit')}
          </AppButton>
        </ButtonResponsiveWrapper>
      </Box>
      <ServerErrorModal
        visible={errorWarningVisible}
        onSetVisible={setErrorWarningVisible}
        onSendRequest={() =>
          onSaveInvestmentAmount({
            amount: investingAmount,
          })
        }
        isLoading={isLoading}
      />
    </ModalWrapper>
  );
};

export default EditAmountModal;
